import { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import EmptyState from '../components/EmptyState';
import { SkeletonCard } from '../components/Skeleton';

// ─── Helpery ──────────────────────────────────────────────────────────────────

const CZAT_STATUS_STYLE = {
  nowa:       { color: '#f59e0b', bg: '#fef3c7', dot: '#f59e0b', labelKey: 'supportConvNew' },
  oczekuje:   { color: '#3b82f6', bg: '#dbeafe', dot: '#3b82f6', labelKey: 'supportConvWaiting' },
  aktywna:    { color: '#16a34a', bg: '#dcfce7', dot: '#16a34a', labelKey: 'supportConvActive' },
  zakonczona: { color: '#6b7280', bg: '#f3f4f6', dot: '#d1d5db', labelKey: 'supportConvDone' },
};

function effectiveStatus(u) {
  if (u.conv_status === 'zakonczona') return 'zakonczona';
  if (u.nieprzeczytane > 0) return 'nowa';
  if (u.last_nadawca === 'user') return 'oczekuje';
  return 'aktywna';
}

function formatTs(s) {
  if (!s) return '';
  const d = new Date(s.replace(' ', 'T') + (s.includes('T') ? '' : 'Z'));
  return d.toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function CzatStatusBadge({ status }) {
  const { t } = useTranslation();
  const s = CZAT_STATUS_STYLE[status] || CZAT_STATUS_STYLE.aktywna;
  const label = t(`dashboardV2.${s.labelKey}`);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '9px', fontWeight: 700, letterSpacing: '0.04em', color: s.color, background: s.bg, borderRadius: '4px', padding: '2px 6px' }}>
      <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: s.dot, display: 'inline-block', flexShrink: 0 }} />
      {label.toUpperCase()}
    </span>
  );
}

// ─── Karta konwersacji ───────────────────────────────────────────────────────

function ConvCard({ u, onClick }) {
  const status = effectiveStatus(u);
  const borderColor = status === 'nowa' ? '#f59e0b' : status === 'oczekuje' ? '#3b82f6' : 'transparent';
  const ts = u.last_at ? new Date(u.last_at.replace(' ', 'T') + (u.last_at.includes('T') ? '' : 'Z')) : null;
  const tsStr = ts ? ts.toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <button onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '12px', width: '100%', textAlign: 'left',
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderLeft: `3px solid ${borderColor}`,
        borderRadius: '8px', padding: '11px 14px', cursor: 'pointer',
        transition: 'border-color 0.15s',
      }}>
      <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: status === 'zakonczona' ? 'var(--bg4)' : 'rgba(184,84,40,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '13px', fontWeight: 700, color: status === 'zakonczona' ? 'var(--ink3)' : 'var(--acc)' }}>
        {(u.firma || u.imie || u.email || '?')[0].toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
          <p style={{ fontSize: '13px', fontWeight: 700, color: status === 'zakonczona' ? 'var(--ink3)' : 'var(--ink)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {u.firma || u.imie || u.email}
          </p>
          <CzatStatusBadge status={status} />
        </div>
        <p style={{ fontSize: '10px', color: 'var(--ink3)', margin: '0 0 3px' }}>{u.email}</p>
        {u.last_msg && (
          <p style={{ fontSize: '11px', color: 'var(--ink2)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {u.last_nadawca === 'admin' ? '↩ ' : ''}{u.last_msg}
          </p>
        )}
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        {u.nieprzeczytane > 0 && (
          <span style={{ display: 'inline-block', minWidth: '20px', height: '20px', borderRadius: '10px', background: '#f59e0b', color: '#fff', fontSize: '10px', fontWeight: 700, lineHeight: '20px', textAlign: 'center', padding: '0 5px', marginBottom: '4px' }}>
            {u.nieprzeczytane}
          </span>
        )}
        <p style={{ fontSize: '9px', color: 'var(--ink3)', margin: 0 }}>{tsStr}</p>
        <p style={{ fontSize: '9px', color: 'var(--ink3)', margin: '2px 0 0' }}>{u.total_msgs} wiad.</p>
      </div>
    </button>
  );
}

function ConvSection({ title, items, onOpen, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  if (items.length === 0) return null;
  return (
    <div style={{ marginBottom: '20px' }}>
      <button onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 8px', width: '100%' }}>
        <span style={{ fontSize: '10px', fontWeight: 700, color: 'var(--ink3)', letterSpacing: '0.08em' }}>{title.toUpperCase()}</span>
        <span style={{ fontSize: '10px', color: 'var(--ink3)', background: 'var(--bg3)', borderRadius: '8px', padding: '1px 7px', fontWeight: 600 }}>{items.length}</span>
        <span style={{ fontSize: '10px', color: 'var(--ink3)', marginLeft: 'auto' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {items.map(u => <ConvCard key={u.id} u={u} onClick={() => onOpen(u)} />)}
        </div>
      )}
    </div>
  );
}

// ─── Główny AdminCzat ────────────────────────────────────────────────────────

export default function AdminCzat() {
  const { t } = useTranslation();
  const [convs, setConvs]       = useState([]);
  const [active, setActive]     = useState(null);
  const [convStatus, setConvStatus] = useState('aktywna');
  const [msgs, setMsgs]         = useState([]);
  const [text, setText]         = useState('');
  const [loading, setLoading]   = useState(true);
  const [sending, setSending]   = useState(false);
  const [acting, setActing]     = useState(false);
  const bottomRef = useRef(null);

  const scrollBottom = () => setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 60);

  const loadConvs = useCallback(async () => {
    try {
      const data = await api.adminGetCzatUsers();
      setConvs(data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    loadConvs();
    const id = setInterval(loadConvs, 15000);
    return () => clearInterval(id);
  }, [loadConvs]);

  const openUser = async (u) => {
    setActive(u);
    setConvStatus(u.conv_status || 'aktywna');
    setMsgs([]); setText('');
    try {
      const data = await api.adminGetCzatUser(u.id);
      setMsgs(data);
      scrollBottom();
      setConvs(prev => prev.map(x => x.id === u.id ? { ...x, nieprzeczytane: 0 } : x));
    } catch {}
  };

  const send = async () => {
    const t = text.trim();
    if (!t || sending || !active) return;
    setSending(true);
    try {
      const msg = await api.adminSendCzat(active.id, t);
      setMsgs(prev => [...prev, msg]);
      setText('');
      scrollBottom();
      setConvStatus('aktywna');
      setConvs(prev => prev.map(x => x.id === active.id
        ? { ...x, last_msg: t, last_nadawca: 'admin', conv_status: 'aktywna', zakonczona_at: null }
        : x));
    } catch {}
    setSending(false);
  };

  const toggleZakoncz = async () => {
    if (!active || acting) return;
    setActing(true);
    try {
      if (convStatus === 'zakonczona') {
        await api.adminWznowCzat(active.id);
        setConvStatus('aktywna');
        setConvs(prev => prev.map(x => x.id === active.id ? { ...x, conv_status: 'aktywna', zakonczona_at: null } : x));
      } else {
        await api.adminZakonczCzat(active.id);
        setConvStatus('zakonczona');
        setConvs(prev => prev.map(x => x.id === active.id ? { ...x, conv_status: 'zakonczona', zakonczona_at: new Date().toISOString() } : x));
        try {
          const data = await api.adminGetCzatUser(active.id);
          setMsgs(data);
          scrollBottom();
        } catch {}
      }
    } catch {}
    setActing(false);
  };

  const handleKey = e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

  // ── Widok rozmowy z konkretnym userem ──
  if (active) {
    const isZakonczona = convStatus === 'zakonczona';
    return (
      <div style={{ maxWidth: '760px' }}>
        {/* Pasek nagłówka */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
          <button onClick={() => setActive(null)} style={{ background: 'none', border: 'none', color: 'var(--acc)', fontSize: '12px', fontWeight: 600, cursor: 'pointer', padding: 0, flexShrink: 0 }}>
            <span className="hidden sm:inline">← Wszystkie konwersacje</span>
            <span className="sm:hidden">← Wszystkie</span>
          </button>
          <span style={{ color: 'var(--border)' }}>|</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(184,84,40,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: 'var(--acc)', flexShrink: 0 }}>
              {(active.firma || active.imie || active.email || '?')[0].toUpperCase()}
            </div>
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {active.firma || active.imie || active.email}
              </p>
              <p style={{ fontSize: '10px', color: 'var(--ink3)', margin: 0 }}>{active.email}</p>
            </div>
            <CzatStatusBadge status={isZakonczona ? 'zakonczona' : effectiveStatus(active)} />
          </div>
          <button onClick={toggleZakoncz} disabled={acting}
            style={{ padding: '6px 14px', borderRadius: '6px', border: `1px solid ${isZakonczona ? 'var(--ok2)' : 'var(--border)'}`, background: 'none', color: isZakonczona ? 'var(--ok2)' : 'var(--err2)', fontSize: '11px', fontWeight: 600, cursor: 'pointer', flexShrink: 0, opacity: acting ? 0.6 : 1 }}>
            {isZakonczona
              ? <><span className="hidden sm:inline">{t('dashboardV2.supportResumeConv')}</span><span className="sm:hidden">{t('dashboardV2.supportResumeConvShort')}</span></>
              : <><span className="hidden sm:inline">{t('dashboardV2.supportEndConv')}</span><span className="sm:hidden">{t('dashboardV2.supportEndConvShort')}</span></>}
          </button>
        </div>

        {isZakonczona && (
          <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: '8px', padding: '10px 14px', marginBottom: '12px' }}>
            <span style={{ fontSize: '12px', color: 'var(--ink3)' }}>
              {t('dashboardV2.supportEndedBanner')}
            </span>
          </div>
        )}

        {/* Bańki wiadomości */}
        <div style={{ height: '500px', overflowY: 'auto', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '16px', marginBottom: '10px', display: 'flex', flexDirection: 'column' }}>
          {msgs.length === 0 && (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <p style={{ fontSize: '12px', color: 'var(--ink3)' }}>{t('dashboardV2.supportEmptyTitle')}</p>
            </div>
          )}
          {msgs.map((msg, i) => {
            const showDate = i === 0 || formatTs(msgs[i - 1].utworzono).slice(0, 8) !== formatTs(msg.utworzono).slice(0, 8);
            const isSystem = msg.system === 1 || msg.system === true;
            if (isSystem) {
              return (
                <div key={msg.id} style={{ margin: '14px 0 18px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                    <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
                    <span style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink3)', background: 'var(--bg3)', padding: '3px 10px', borderRadius: '10px', border: '1px solid var(--border)' }}>
                      {t('dashboardV2.supportConvEndedAt', { date: formatTs(msg.utworzono) })}
                    </span>
                    <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
                  </div>
                  <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderLeft: '3px solid var(--acc)', borderRadius: '8px', padding: '12px 14px' }}>
                    <p style={{ fontSize: '9px', fontWeight: 700, color: 'var(--acc)', margin: '0 0 4px', letterSpacing: '0.05em' }}>SCANPILOTS · auto</p>
                    <p style={{ fontSize: '12px', lineHeight: 1.55, margin: 0, color: 'var(--ink2)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {msg.tresc}
                    </p>
                  </div>
                </div>
              );
            }
            return (
              <div key={msg.id}>
                {showDate && (
                  <div style={{ textAlign: 'center', margin: '8px 0' }}>
                    <span style={{ fontSize: '9px', color: 'var(--ink3)', background: 'var(--bg3)', borderRadius: '8px', padding: '2px 8px' }}>
                      {formatTs(msg.utworzono).slice(0, 8)}
                    </span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: msg.nadawca === 'admin' ? 'flex-end' : 'flex-start', marginBottom: '8px' }}>
                  <div style={{ maxWidth: '75%', background: msg.nadawca === 'admin' ? 'var(--acc)' : 'var(--bg3)', border: msg.nadawca === 'admin' ? 'none' : '1px solid var(--border)', borderRadius: msg.nadawca === 'admin' ? '14px 14px 4px 14px' : '14px 14px 14px 4px', padding: '9px 13px' }}>
                    {msg.nadawca === 'user' && (
                      <p style={{ fontSize: '9px', fontWeight: 700, color: 'var(--ink3)', marginBottom: '4px', letterSpacing: '0.04em' }}>
                        {active.firma || active.imie || active.email}
                      </p>
                    )}
                    <p style={{ fontSize: '13px', lineHeight: 1.55, margin: 0, color: msg.nadawca === 'admin' ? '#fff' : 'var(--ink)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {msg.tresc}
                    </p>
                    <p style={{ fontSize: '9px', margin: '5px 0 0', color: msg.nadawca === 'admin' ? 'rgba(255,255,255,0.55)' : 'var(--ink3)', textAlign: 'right' }}>
                      {formatTs(msg.utworzono).slice(9)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {isZakonczona ? (
          <div style={{ textAlign: 'center', padding: '12px', color: 'var(--ink3)', fontSize: '11px' }}>
            {t('dashboardV2.supportResumeToReply')}
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
            <textarea value={text} onChange={e => setText(e.target.value)} onKeyDown={handleKey}
              placeholder={t('dashboardV2.supportSendPlaceholder')} rows={3}
              style={{ flex: 1, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--ink)', fontSize: '13px', padding: '10px 12px', resize: 'none', outline: 'none', lineHeight: 1.5, fontFamily: 'inherit' }} />
            <button onClick={send} disabled={!text.trim() || sending}
              style={{ padding: '10px 18px', borderRadius: '8px', border: 'none', background: text.trim() && !sending ? 'var(--acc)' : 'var(--bg4)', color: text.trim() && !sending ? '#fff' : 'var(--ink3)', fontSize: '12px', fontWeight: 700, cursor: text.trim() && !sending ? 'pointer' : 'not-allowed', height: '44px', flexShrink: 0 }}>
              {sending ? '…' : t('dashboardV2.supportSendButton')}
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── Lista konwersacji ──
  const nowe       = convs.filter(u => effectiveStatus(u) === 'nowa');
  const oczekuje   = convs.filter(u => effectiveStatus(u) === 'oczekuje');
  const aktywne    = convs.filter(u => effectiveStatus(u) === 'aktywna');
  const zakonczone = convs.filter(u => effectiveStatus(u) === 'zakonczona');
  const totalToHandle = nowe.length + oczekuje.length;

  return (
    <div style={{ maxWidth: '760px' }}>
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--ink)', margin: '0 0 4px' }}>{t('dashboardV2.supportTitle')}</h1>
        <p style={{ fontSize: '12px', color: 'var(--ink3)', margin: 0 }}>
          {convs.length === 0
            ? t('dashboardV2.supportNoMessages')
            : totalToHandle > 0
              ? t('dashboardV2.supportSomeWaiting', { n: totalToHandle, count: totalToHandle })
              : t('dashboardV2.supportAllHandled')}
        </p>
      </div>

      {/* Statystyki — zawsze widoczne */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {[
          { label: t('dashboardV2.supportStatNew'),     count: nowe.length,       color: '#f59e0b' },
          { label: t('dashboardV2.supportStatWaiting'), count: oczekuje.length,   color: '#3b82f6' },
          { label: t('dashboardV2.supportStatActive'),  count: aktywne.length,    color: '#16a34a' },
          { label: t('dashboardV2.supportStatDone'),    count: zakonczone.length, color: '#6b7280' },
        ].map(({ label, count, color }) => (
          <div key={label} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '8px', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: color, display: 'inline-block' }} />
            <span style={{ fontSize: '11px', color: 'var(--ink3)' }}>{label}</span>
            <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)' }}>{count}</span>
          </div>
        ))}
      </div>

      {loading && <SkeletonCard lines={3} />}

      {!loading && convs.length === 0 && (
        <EmptyState
          icon={
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          }
          title={t('dashboardV2.supportEmptyTitle')}
          description={t('dashboardV2.supportEmptyDesc')}
        />
      )}

      {!loading && convs.length > 0 && (
        <>
          <ConvSection title={t('dashboardV2.supportSectionNew')}     items={nowe}       onOpen={openUser} defaultOpen />
          <ConvSection title={t('dashboardV2.supportSectionWaiting')} items={oczekuje}  onOpen={openUser} defaultOpen />
          <ConvSection title={t('dashboardV2.supportSectionActive')}  items={aktywne}    onOpen={openUser} defaultOpen />
          <ConvSection title={t('dashboardV2.supportSectionDone')}    items={zakonczone} onOpen={openUser} defaultOpen={zakonczone.length <= 5} />
        </>
      )}
    </div>
  );
}
